// src/components/dashboard/GenerateAccountCard.jsx
//
// Complete-profile card. Dynamically determines which steps are needed based
// on existing user data:
//   - Business Name step: shown only if user has no company_name
//   - Location step: shown only if user has no company_address
//   - Bank Account step: always shown
//
// After completing a step, user data is refreshed and steps are recomputed
// so the modal and "Complete Your Profile" card update without a page reload.
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { showToast } from '../../utils/helpers';
import api from '../../utils/api';
import { API_CONFIG } from '../../utils/apiConfig';
import { useAuth } from '../../contexts/AuthContext';
import BankAccountsCard from './BankAccountsCard';
import LocationPicker from '../LocationPicker';

const GenerateAccountCard = ({ user, onCompleteProfile }) => {
  const { logout, refreshUserData } = useAuth();
  const [showModal, setShowModal] = useState(false);
  const [currentStepKey, setCurrentStepKey] = useState(null); // tracks current step by key: 'name', 'location', 'bank'
  const [formData, setFormData] = useState({
    companyName: ''
  });
  const [stepsNeeded, setStepsNeeded] = useState([]); // dynamically computed

  // Helper: get the first step key from stepsNeeded
  const getFirstStepKey = useCallback((steps) => {
    return steps.length > 0 ? steps[0].key : null;
  }, []);

  // Determine which steps are needed based on existing user data
  const computeStepsNeeded = useCallback((userData) => {
    const steps = [];
    if (!userData?.company_name || userData.company_name.trim() === '') {
      steps.push({ id: 1, key: 'name', label: 'Business Name' });
    }
    if (!userData?.company_address || userData.company_address.trim() === '') {
      steps.push({ id: steps.length + 1, key: 'location', label: 'Location' });
    }
    // Bank account is always shown
    steps.push({ id: steps.length + 1, key: 'bank', label: 'Bank Account' });
    return steps;
  }, []);
  const [isLoading, setIsLoading] = useState(false);
  const [isCheckingBusiness, setIsCheckingBusiness] = useState(false);
  const [businessNameStatus, setBusinessNameStatus] = useState(''); // 'available', 'taken', 'registered', 'checking'
  const [businessNameError, setBusinessNameError] = useState('');
  const [isBusinessRegistered, setIsBusinessRegistered] = useState(false);
  const [requiresBusinessVerification, setRequiresBusinessVerification] = useState(false);
  const [verificationData, setVerificationData] = useState({
    rc_number: '',
    business_certificate: null,
    ownsBusiness: false
  });
  const [filePreview, setFilePreview] = useState(null);
  const [bankConnected, setBankConnected] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(null); // { lat, lon, name }

  const businessCheckTimeout = useRef(null);
  const companyNameInputRef = useRef(null);
  const fileInputRef = useRef(null);

  // Constants
  const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
  const ALLOWED_FILE_TYPES = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];

  // Business status configurations
  const BUSINESS_STATUS_CONFIG = {
    checking: { color: 'blue', icon: 'spinner', message: 'Checking business name availability...' },
    available: { color: 'green', icon: 'check', message: 'Business name is available' },
    registered: { color: 'yellow', icon: 'warning', message: 'This business name is registered with CAC.' },
    error: { color: 'red', icon: 'error', message: 'Error checking business name. Please try again.' }
  };

  // Pre-fill the name they signed up with (editable) and compute needed steps
  useEffect(() => {
    if (user) {
      setFormData(prev => ({
        ...prev,
        companyName: user.company_name || user.username || user.fullname || ''
      }));
      const needed = computeStepsNeeded(user);
      setStepsNeeded(needed);
      // Start at the first step that needs completing
      if (needed.length > 0) {
        setCurrentStepKey(needed[0].key);
      }
    }
  }, [user, computeStepsNeeded]);

  useEffect(() => {
    return () => {
      if (businessCheckTimeout.current) {
        clearTimeout(businessCheckTimeout.current);
      }
    };
  }, []);

  // Memoized form validation
  const isFormReady = useCallback(() => {
    const hasName = formData.companyName.trim();
    if (!hasName) return false;

    // If business check hasn't been triggered yet, allow submission
    if (!businessNameStatus) {
      return true;
    }

    // If business check is in progress, don't allow submission
    if (businessNameStatus === 'checking' || isCheckingBusiness) {
      return false;
    }

    // If business is registered, check verification requirements
    if (businessNameStatus === 'registered') {
      return verificationData.ownsBusiness &&
             verificationData.rc_number.trim() &&
             verificationData.business_certificate;
    }

    // For available or other statuses, allow submission
    return true;
  }, [formData, businessNameStatus, isCheckingBusiness, verificationData]);

  // Input change handler - only updates form data, doesn't trigger API calls
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (type === 'checkbox') {
      setVerificationData(prev => ({ ...prev, [name]: checked }));
    } else if (name === 'companyName') {
      setFormData(prev => ({ ...prev, [name]: value }));
      // Reset status when user starts typing new content
      if (businessNameStatus && !value.includes(formData.companyName)) {
        setBusinessNameStatus('');
        setBusinessNameError('');
        setIsBusinessRegistered(false);
      }
    } else {
      setVerificationData(prev => ({ ...prev, [name]: value }));
    }
  };

  // Handle blur event on company name field
  const handleCompanyNameBlur = () => {
    if (formData.companyName.trim().length > 2) {
      checkBusinessNameAvailability(formData.companyName.trim());
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file size (10MB max)
    if (file.size > MAX_FILE_SIZE) {
      showToast('File size must be less than 10MB', 'error');
      return;
    }

    // Validate file type
    if (!ALLOWED_FILE_TYPES.includes(file.type)) {
      showToast('Please upload a PDF, JPG, or PNG file', 'error');
      return;
    }

    setVerificationData(prev => ({
      ...prev,
      business_certificate: file
    }));

    // Create preview
    if (file.type === 'application/pdf') {
      setFilePreview({
        type: 'pdf',
        name: file.name,
        size: file.size
      });
    } else if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setFilePreview({
          type: 'image',
          url: e.target.result,
          name: file.name,
          size: file.size
        });
      };
      reader.readAsDataURL(file);
    }
  };

  const removeFile = () => {
    setVerificationData(prev => ({
      ...prev,
      business_certificate: null
    }));
    setFilePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Check business name availability and registration status
  const checkBusinessNameAvailability = async (businessName) => {
    if (!businessName || businessName.length < 3) {
      setBusinessNameStatus('');
      return;
    }

    setIsCheckingBusiness(true);
    setBusinessNameStatus('checking');
    setBusinessNameError('');

    try {
      const response = await api.post(API_CONFIG.AUTH.IS_REGISTERED_BUSINESS, {
        businessName: businessName,
        userId: user?.id
      });
      const data = await response.data;

      if (data.isRegistered) {
        setBusinessNameStatus('registered');
        setBusinessNameError('This business name is registered with CAC. If you own this business, please check the box below and provide verification documents.');
        setIsBusinessRegistered(true);
        setRequiresBusinessVerification(true);
      } else {
        setBusinessNameStatus('available');
        setBusinessNameError('');
        setIsBusinessRegistered(false);
        setRequiresBusinessVerification(false);
      }
    } catch (error) {
      console.error('Business check error:', error);
      setBusinessNameStatus('error');
      setBusinessNameError('Error checking business name. Please try again.');
      setIsBusinessRegistered(false);
      setRequiresBusinessVerification(false);
    } finally {
      setIsCheckingBusiness(false);
    }
  };

  // Step 1: confirm (and optionally update) the business name, then move to
  // the connect-bank-account step.
  const handleConfirmName = async () => {
    const name = formData.companyName.trim();
    if (name.length < 3) {
      showToast('Please enter a business name', 'error');
      return;
    }
    if (businessNameStatus === 'checking' || isCheckingBusiness) {
      showToast('Please wait for business name verification to complete', 'error');
      return;
    }
    if (businessNameStatus === 'registered' && !verificationData.ownsBusiness) {
      showToast('Please confirm business ownership to continue', 'error');
      return;
    }
    if (businessNameStatus === 'registered' && verificationData.ownsBusiness && (!verificationData.rc_number.trim() || !verificationData.business_certificate)) {
      showToast('RC number and business certificate are required for registered businesses', 'error');
      return;
    }

    setIsLoading(true);
    try {
      // Only hit the API when the name actually changed — otherwise the name
      // they signed up with is already saved.
      const nameChanged = name !== (user?.company_name || '');
      if (nameChanged) {
        const submitData = new FormData();
        submitData.append('company', name);
        submitData.append('fullname', user?.fullname || '');
        submitData.append('phonenumber', user?.company_phone || user?.phoneNumber || '');
        submitData.append('email', user?.company_email || user?.email || '');
        if (verificationData.ownsBusiness) {
          submitData.append('ownsBusiness', 'true');
          submitData.append('rc_number', verificationData.rc_number);
          if (verificationData.business_certificate) {
            submitData.append('business_certificate', verificationData.business_certificate);
          }
        }

        const response = await api.post(API_CONFIG.USER.UPDATE_PERSONAL_DETAILS, submitData);
        const data = await response.data;

        if (data.error) {
          showToast(data.error, 'error');
          return;
        }
        if (data.requiresVerification) {
          showToast(data.success || 'Business name change submitted and is pending verification.', 'success');
        } else {
          showToast(data.success || 'Business name saved successfully', 'success');
        }
        await refreshUserData();
      }

      // Name confirmed — cue the next needed step.
      const updatedUser = { ...user, company_name: name };
      const updatedSteps = computeStepsNeeded(updatedUser);
      setStepsNeeded(updatedSteps);
      // Find the next step (skip name since we just completed it)
      const nextStep = updatedSteps.find(s => s.key !== 'name');
      if (nextStep) {
        setCurrentStepKey(nextStep.key);
      } else {
        // All steps complete
        handleFinish();
      }
    } catch (error) {
      console.error('Error confirming business name:', error);
      showToast(error.response?.data?.error || 'Failed to save business name. Please try again.', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  // Step 2: save the selected address to user_accounts.company_address, then
  // cue the connect-bank-account step.
  const handleSaveLocation = async () => {
    if (!selectedLocation) {
      showToast('Please pick your business address to continue', 'error');
      return;
    }
    setIsLoading(true);
    try {
      const response = await api.post(API_CONFIG.USER.UPDATE_COMPANY_ADDRESS, {
        address: selectedLocation.name,
        latitude: selectedLocation.lat,
        longitude: selectedLocation.lon,
      });
      const data = response.data;
      if (data.error) {
        showToast(data.error, 'error');
        return;
      }
      await refreshUserData();
      const updatedUser = { ...user, company_address: selectedLocation.name };
      const updatedSteps = computeStepsNeeded(updatedUser);
      setStepsNeeded(updatedSteps);
      // Find the next step (skip location since we just completed it)
      const nextStep = updatedSteps.find(s => s.key !== 'location' && s.key !== 'name');
      if (nextStep) {
        setCurrentStepKey(nextStep.key);
      } else {
        handleFinish();
      }
    } catch (error) {
      console.error('Error saving location:', error);
      showToast(error.response?.data?.error || 'Failed to save your address. Please try again.', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  // Step 3: bank account connected (or already present). Fired by
  // BankAccountsCard after a successful connect and on load when accounts
  // exist, so the Continue button reflects reality.
  const handleBankConnected = () => {
    setBankConnected(true);
  };

  const handleFinish = () => {
    if (onCompleteProfile) {
      onCompleteProfile();
    } else {
      handleCloseModal();
    }
  };

  const resetForm = () => {
    setFormData({
      companyName: user?.company_name || user?.username || user?.fullname || ''
    });
    setVerificationData({
      rc_number: '',
      business_certificate: null,
      ownsBusiness: false
    });
    setBusinessNameStatus('');
    setBusinessNameError('');
    setIsBusinessRegistered(false);
    setRequiresBusinessVerification(false);
    setIsCheckingBusiness(false);
    setFilePreview(null);
    setBankConnected(false);
    setSelectedLocation(null);
    // Recompute steps with current user data
    const needed = computeStepsNeeded(user);
    setStepsNeeded(needed);
    setCurrentStepKey(needed.length > 0 ? needed[0].key : null);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    resetForm();
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Status icon renderer
  const renderStatusIcon = () => {
    const status = BUSINESS_STATUS_CONFIG[businessNameStatus];
    if (!status) return null;

    switch (status.icon) {
      case 'spinner':
        return <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600" />;
      case 'check':
        return (
          <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
          </svg>
        );
      case 'warning':
        return (
          <svg className="w-5 h-5 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.35 16.5c-.77.833.192 2.5 1.732 2.5z" />
          </svg>
        );
      case 'error':
        return (
          <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        );
      default:
        return null;
    }
  };

  const getInputStyles = () => {
    const baseStyles = "w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 pr-10 transition-colors";

    if (isCheckingBusiness) {
      return `${baseStyles} bg-gray-100 border-gray-300 cursor-not-allowed`;
    }

    const status = BUSINESS_STATUS_CONFIG[businessNameStatus];
    if (status) {
      if (status.color === 'green') {
        return `${baseStyles} border-green-300 bg-green-50`;
      } else if (status.color === 'yellow') {
        return `${baseStyles} border-yellow-300 bg-yellow-50`;
      } else if (status.color === 'red') {
        return `${baseStyles} border-red-300 bg-red-50`;
      } else if (status.color === 'blue') {
        return `${baseStyles} border-blue-300 bg-blue-50`;
      }
    }

    return `${baseStyles} border-gray-300`;
  };

  return (
    <>
      {/* Complete Profile Card */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="text-center">
          <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
          </div>

          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            Complete Your Profile
          </h3>

          <p className="text-gray-600 mb-4">
            {stepsNeeded.some(s => s.key === 'name') && stepsNeeded.some(s => s.key === 'location') && (
              'Confirm your business name, set your location, and connect a bank account to start receiving payments.'
            )}
            {stepsNeeded.some(s => s.key === 'name') && !stepsNeeded.some(s => s.key === 'location') && (
              'Confirm your business name and connect a bank account to start receiving payments.'
            )}
            {!stepsNeeded.some(s => s.key === 'name') && stepsNeeded.some(s => s.key === 'location') && (
              'Set your location and connect a bank account to start receiving payments.'
            )}
            {!stepsNeeded.some(s => s.key === 'name') && !stepsNeeded.some(s => s.key === 'location') && (
              'Connect a bank account to start receiving payments.'
            )}
          </p>

          <button
            onClick={() => setShowModal(true)}
            className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
          >
            Complete Profile
          </button>

          <button
            className="ml-4 bg-gradient-to-r from-gray-600 to-gray-700 text-white px-8 py-3 rounded-xl hover:from-gray-700 hover:to-gray-800 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-all duration-200 shadow-sm hover:shadow-md font-medium"
            onClick={() => { logout(); }}
          >
            Logout
          </button>
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-gray-900">
                  Complete Your Profile
                </h3>
                <button
                  onClick={handleCloseModal}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Step Indicator */}
              <div className="flex items-center gap-2 mb-6">
                {stepsNeeded.map((step, index) => {
                  const currentIndex = stepsNeeded.findIndex(s => s.key === currentStepKey);
                  const isCompleted = index < currentIndex;
                  const isCurrent = step.key === currentStepKey;
                  return (
                    <div key={step.key} className="flex items-center gap-2">
                      <div
                        className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-colors ${
                          isCompleted || isCurrent ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-500'
                        }`}
                      >
                        {isCompleted ? (
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" /></svg>
                        ) : (
                          index + 1
                        )}
                      </div>
                      <span className={`text-sm font-medium ${isCurrent ? 'text-gray-900' : 'text-gray-400'}`}>
                        {step.label}
                      </span>
                      {index < stepsNeeded.length - 1 && <div className="w-8 h-0.5 bg-gray-200 ml-2" />}
                    </div>
                  );
                })}
              </div>

              {/* Step 1: Confirm Business Name */}
              {currentStepKey === 'name' && (
                <div className="space-y-6">
                  <div>
                    <p className="text-sm text-gray-500 mb-1">
                      Confirm the business name you signed up with. You can edit it if needed.
                    </p>
                    <label htmlFor="companyName" className="block text-sm font-medium text-gray-700 mb-2">
                      Company / Business Name *
                    </label>
                    <div className="relative">
                      <input
                        ref={companyNameInputRef}
                        type="text"
                        id="companyName"
                        name="companyName"
                        value={formData.companyName}
                        onChange={handleInputChange}
                        onBlur={handleCompanyNameBlur}
                        className={getInputStyles()}
                        placeholder="Enter your Company / Business Name"
                        required
                      />
                      <div className="absolute right-3 top-3">
                        {renderStatusIcon()}
                      </div>
                    </div>

                    {businessNameStatus && (
                      <p className={`text-${BUSINESS_STATUS_CONFIG[businessNameStatus]?.color}-600 text-sm mt-2 flex items-center`}>
                        {BUSINESS_STATUS_CONFIG[businessNameStatus]?.message}
                      </p>
                    )}
                    {businessNameStatus === 'registered' && (
                      <p className="text-yellow-600 text-sm mt-2">{businessNameError}</p>
                    )}
                  </div>

                  {/* Business Verification (only shown for registered businesses) */}
                  {isBusinessRegistered && (
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-5 space-y-4">
                      <div>
                        <h4 className="font-semibold text-yellow-800 text-lg mb-1">Business Verification Required</h4>
                        <p className="text-yellow-700 text-sm">
                          This business name is registered with CAC. Please verify ownership to prevent fraud and ensure account security.
                        </p>
                      </div>

                      <label className="flex items-start space-x-3 p-3 bg-white rounded-lg border border-yellow-300">
                        <input
                          type="checkbox"
                          name="ownsBusiness"
                          checked={verificationData.ownsBusiness}
                          onChange={handleInputChange}
                          className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 mt-0.5"
                        />
                        <span className="text-sm text-yellow-800">
                          I confirm that I am the legal owner or authorized representative of this registered business
                        </span>
                      </label>

                      {verificationData.ownsBusiness && (
                        <>
                          <div>
                            <label className="block text-sm font-medium text-yellow-800 mb-2">
                              RC Number *
                            </label>
                            <input
                              type="text"
                              name="rc_number"
                              value={verificationData.rc_number}
                              onChange={handleInputChange}
                              className="w-full px-4 py-3 border border-yellow-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 bg-white"
                              placeholder="Enter your RC number"
                              required
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-yellow-800 mb-2">
                              Business Certificate *
                            </label>

                            {!filePreview ? (
                              <div
                                className="border-2 border-dashed border-yellow-300 rounded-lg p-6 text-center cursor-pointer hover:border-yellow-400 transition-colors bg-white"
                                onClick={() => fileInputRef.current?.click()}
                              >
                                <svg className="w-8 h-8 text-yellow-500 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                                </svg>
                                <p className="text-yellow-700 text-sm mb-1">Click to upload business certificate</p>
                                <p className="text-yellow-600 text-xs">PDF, JPG, PNG (Max 10MB)</p>
                              </div>
                            ) : (
                              <div className="bg-white rounded-lg border border-yellow-300 p-4">
                                <div className="flex items-center justify-between mb-3">
                                  <span className="text-sm font-medium text-yellow-800">Uploaded File</span>
                                  <button
                                    type="button"
                                    onClick={removeFile}
                                    className="text-yellow-600 hover:text-yellow-800"
                                  >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                  </button>
                                </div>

                                {filePreview.type === 'pdf' && (
                                  <div className="flex items-center space-x-3 p-3 bg-yellow-100 rounded-lg">
                                    <div className="w-10 h-12 bg-red-500 rounded flex items-center justify-center">
                                      <span className="text-white text-xs font-bold">PDF</span>
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <p className="text-sm font-medium text-yellow-900 truncate">{filePreview.name}</p>
                                      <p className="text-xs text-yellow-700">{formatFileSize(filePreview.size)}</p>
                                    </div>
                                  </div>
                                )}

                                {filePreview.type === 'image' && (
                                  <div className="space-y-2">
                                    <div className="relative bg-gray-100 rounded-lg overflow-hidden">
                                      <img
                                        src={filePreview.url}
                                        alt="Preview"
                                        className="w-full h-32 object-contain"
                                      />
                                    </div>
                                    <div className="flex justify-between items-center">
                                      <span className="text-sm text-yellow-800 truncate flex-1 mr-2">{filePreview.name}</span>
                                      <span className="text-xs text-yellow-600 whitespace-nowrap">{formatFileSize(filePreview.size)}</span>
                                    </div>
                                  </div>
                                )}
                              </div>
                            )}

                            <input
                              ref={fileInputRef}
                              type="file"
                              name="business_certificate"
                              accept=".pdf,.jpg,.jpeg,.png"
                              onChange={handleFileChange}
                              className="hidden"
                              required={verificationData.ownsBusiness}
                            />

                            <p className="text-yellow-600 text-xs mt-2">
                              Accepted formats: PDF, JPG, PNG (Maximum file size: 10MB)
                            </p>
                          </div>
                        </>
                      )}
                    </div>
                  )}

                  <div className="flex space-x-3 pt-4">
                    <button
                      type="button"
                      onClick={handleCloseModal}
                      className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={handleConfirmName}
                      disabled={!isFormReady() || isLoading}
                      className="flex-1 bg-blue-600 text-white py-3 px-4 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isLoading ? (
                        <div className="flex items-center justify-center">
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                          Saving...
                        </div>
                      ) : (
                        'Confirm & Continue'
                      )}
                    </button>
                  </div>
                </div>
              )}

              {/* Step 2: Set Business Location */}
              {currentStepKey === 'location' && (
                <div className="space-y-5">
                  <div>
                    <h4 className="font-bold text-gray-900 mb-1">Where is your business located?</h4>
                    <p className="text-sm text-gray-500">
                      Pick your address from the suggestions, use your current location, or click
                      anywhere on the map.
                    </p>
                  </div>

                  <LocationPicker
                    initialQuery={user?.company_address || ''}
                    onSelect={setSelectedLocation}
                  />

                  <div className="flex space-x-3 pt-2">
                    <button
                      type="button"
                      onClick={() => {
                        // Go back to the previous step in stepsNeeded
                        const currentIndex = stepsNeeded.findIndex(s => s.key === 'location');
                        if (currentIndex > 0) {
                          setCurrentStepKey(stepsNeeded[currentIndex - 1].key);
                        }
                      }}
                      className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
                    >
                      Back
                    </button>
                    <button
                      type="button"
                      onClick={handleSaveLocation}
                      disabled={!selectedLocation || isLoading}
                      className="flex-1 bg-blue-600 text-white py-3 px-4 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isLoading ? (
                        <div className="flex items-center justify-center">
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                          Saving...
                        </div>
                      ) : (
                        'Save & Continue'
                      )}
                    </button>
                  </div>
                </div>
              )}

              {/* Step 3: Connect Bank Account */}
              {currentStepKey === 'bank' && (
                <div className="space-y-5">
                  <div className="flex items-start gap-3 p-4 rounded-xl bg-blue-50 border border-blue-200">
                    <svg className="w-6 h-6 text-blue-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                    </svg>
                    <div>
                      <h4 className="font-bold text-blue-900">
                        Almost there, connect your bank account to start receiving payment
                      </h4>
                      <p className="text-sm text-blue-700 mt-0.5">
                        Payments are sent to the bank account you connect below.
                      </p>
                    </div>
                  </div>

                  <BankAccountsCard onAccountConnected={handleBankConnected} />

                  {bankConnected && (
                    <div className="flex items-center gap-2 p-3 rounded-lg bg-green-50 border border-green-200 text-green-700 text-sm">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                      </svg>
                      Bank account connected! You're ready to receive payments.
                    </div>
                  )}

                  <div className="flex space-x-3 pt-2">
                    <button
                      type="button"
                      onClick={() => {
                        // Go back to the previous step in stepsNeeded
                        const currentIndex = stepsNeeded.findIndex(s => s.key === 'bank');
                        if (currentIndex > 0) {
                          setCurrentStepKey(stepsNeeded[currentIndex - 1].key);
                        }
                      }}
                      className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
                    >
                      Back
                    </button>
                    <button
                      type="button"
                      onClick={handleFinish}
                      disabled={!bankConnected}
                      className="flex-1 bg-blue-600 text-white py-3 px-4 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Continue
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default GenerateAccountCard;
